import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import {
  CreateDuvidaEadDto,
  DuvidaEadItem,
  ResponderDuvidaEadDto,
  ToggleReacaoDuvidaDto,
  UpdateDuvidaEadDto,
} from "@/interfaces/ensino-ead.interface";
import { processarDuvidaEadItem } from "@/components/ensino/ead/ead-comentarios.utils";
import { RESOURCES } from "./resources";

const ensinoDuvidasBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const { baseQueryWithReauth } = await import("@/lib/api");
  return baseQueryWithReauth(args, api, extraOptions);
};

function processarLista(
  response: DuvidaEadItem[],
  usuarioLogadoId?: string,
): DuvidaEadItem[] {
  if (!Array.isArray(response)) return [];
  return response.map((d) => processarDuvidaEadItem(d, usuarioLogadoId));
}

export const ensinoDuvidasAPI = createApi({
  reducerPath: "ensinoDuvidasAPI",
  baseQuery: ensinoDuvidasBaseQuery,
  tagTypes: [RESOURCES.ENSINO_DUVIDAS],
  endpoints: (build) => ({
    listarDuvidasTurma: build.query<
      DuvidaEadItem[],
      {
        eventoId: number;
        loginId?: number;
        reuniaoId?: number;
        apenasAbertas?: boolean;
        usuarioLogadoId?: string;
      }
    >({
      query: ({ eventoId, loginId, reuniaoId, apenasAbertas }) => ({
        url: `/ensino-duvidas/turmas/${eventoId}`,
        params: {
          ...(loginId ? { loginId } : {}),
          ...(reuniaoId ? { reuniaoId } : {}),
          ...(apenasAbertas ? { apenasAbertas: "true" } : {}),
        },
      }),
      transformResponse: (response: DuvidaEadItem[], _meta, arg) =>
        processarLista(response, arg.usuarioLogadoId),
      providesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_DUVIDAS, id: `turma-${eventoId}` },
      ],
    }),

    contarDuvidasPendentes: build.query<number, { eventoId: number; loginId?: number }>({
      query: ({ eventoId, loginId }) => ({
        url: `/ensino-duvidas/turmas/${eventoId}/pendentes/count`,
        params: loginId ? { loginId } : undefined,
      }),
      providesTags: (_r, _e, { eventoId }) => [
        { type: RESOURCES.ENSINO_DUVIDAS, id: `pendentes-${eventoId}` },
      ],
    }),

    criarDuvidaEad: build.mutation<DuvidaEadItem, CreateDuvidaEadDto>({
      query: (body) => ({
        url: `/ensino-duvidas`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, body) => [
        { type: RESOURCES.ENSINO_DUVIDAS, id: `turma-${body.EventoId}` },
        { type: RESOURCES.ENSINO_DUVIDAS, id: `pendentes-${body.EventoId}` },
      ],
    }),

    responderDuvidaEad: build.mutation<
      DuvidaEadItem,
      { id: number; loginId?: number; body: ResponderDuvidaEadDto }
    >({
      query: ({ id, loginId, body }) => ({
        url: `/ensino-duvidas/${id}/responder`,
        method: "POST",
        params: loginId ? { loginId } : undefined,
        body,
      }),
      invalidatesTags: [RESOURCES.ENSINO_DUVIDAS],
    }),

    reagirComentarioEad: build.mutation<
      { action: "added" | "removed" },
      { duvidaId: number; loginId: number; body: ToggleReacaoDuvidaDto }
    >({
      query: ({ duvidaId, loginId, body }) => ({
        url: `/ensino-duvidas/reacoes/${duvidaId}`,
        method: "POST",
        params: { loginId },
        body,
      }),
      invalidatesTags: [RESOURCES.ENSINO_DUVIDAS],
    }),

    marcarDuvidaResolvida: build.mutation<DuvidaEadItem, { id: number; loginId?: number }>({
      query: ({ id, loginId }) => ({
        url: `/ensino-duvidas/${id}/resolvida`,
        method: "PATCH",
        params: loginId ? { loginId } : undefined,
      }),
      invalidatesTags: [RESOURCES.ENSINO_DUVIDAS],
    }),

    editarDuvidaEad: build.mutation<{ Id: number }, { id: number; loginId: number; body: UpdateDuvidaEadDto }>({
      query: ({ id, loginId, body }) => ({
        url: `/ensino-duvidas/${id}`,
        method: "PATCH",
        params: { loginId },
        body,
      }),
      invalidatesTags: [RESOURCES.ENSINO_DUVIDAS],
    }),

    excluirDuvidaEad: build.mutation<{ Id: number }, { id: number; loginId: number }>({
      query: ({ id, loginId }) => ({
        url: `/ensino-duvidas/${id}`,
        method: "DELETE",
        params: { loginId },
      }),
      invalidatesTags: [RESOURCES.ENSINO_DUVIDAS],
    }),
  }),
});

export const {
  useListarDuvidasTurmaQuery,
  useContarDuvidasPendentesQuery,
  useCriarDuvidaEadMutation,
  useResponderDuvidaEadMutation,
  useReagirComentarioEadMutation,
  useMarcarDuvidaResolvidaMutation,
  useEditarDuvidaEadMutation,
  useExcluirDuvidaEadMutation,
} = ensinoDuvidasAPI;
